import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import _ from 'underscore';
import cx from 'classnames';
import { getAudioFileUrl } from '@culture/utils/helpers/files';
import { getImageUrl } from '@culture/common-helpers/cultureUtils';

import getConfig from 'next/config';

import { ImageFormatContext } from 'contexts/ImageFormatContext';

import AudioPlayer from 'components/Audio/AudioPlayer';
import Icon from 'components/Icon';
import StyledContentImage from 'components/StyledContent/components/StyledContentImage';
import StyledContentVideo from 'components/StyledContent/components/StyledContentVideo';

import { VariantPropType } from 'propTypes/common';

import { getRatioImage } from 'utils/helpers/images';

// eslint-disable-next-line import/no-cycle
import StyledContentBlock from 'components/StyledContent/components/StyledContentBlock';
import styles from './styles.module.scss';

const { publicRuntimeConfig } = getConfig();

const Variant = ({
	variant,
	selectedVariants,
	selectVariant,
	questionNumber,
	isTestCorrect,
	isQuestionSelected,
	interactiveMode
}) => {
	const imageFormat = useContext(ImageFormatContext);
	const { audio, position, isCorrect, jsonText, video, image } = variant;

	const isSelectedVariant =
		selectedVariants[questionNumber]?.position === position;

	const isVariantCorrect = isSelectedVariant && isCorrect;
	const isIncorrect =
		isSelectedVariant && typeof isCorrect === 'boolean' && !isCorrect;

	const showCorrected = !isVariantCorrect && isQuestionSelected && isCorrect;

	const getTheme = () => {
		switch (interactiveMode) {
			case 'list': {
				if (isVariantCorrect || (!isTestCorrect && isSelectedVariant)) {
					return 'correct';
				}
				if (isIncorrect) {
					return 'incorrect';
				}
				if (showCorrected) {
					return 'corrected';
				}
				return 'neutral';
			}
			case 'slider': {
				return 'neutral';
			}
			default:
				break;
		}
	};

	const iconToShow =
		interactiveMode === 'slider' ||
		(isTestCorrect && isCorrect) ||
		typeof isCorrect === 'undefined'
			? 'correct'
			: 'close-bold';

	const isDisabled =
		interactiveMode === 'list' && isTestCorrect && isQuestionSelected;

	const theme = getTheme();

	const imageSrc =
		image &&
		getImageUrl(image, {
			format: imageFormat,
			params: {
				crop: 'fill',
				gravity: 'center'
			}
		});

	const trackUrl =
		!_.isEmpty(audio) &&
		getAudioFileUrl(audio, {
			config: publicRuntimeConfig
		});

	return (
		<li className={styles.Variant}>
			<button
				className={cx(
					styles.Variant__Button,
					styles[`Variant__Button_${theme}`],
					{
						[styles.Variant__Button_disabled]: isDisabled
					}
				)}
				onClick={() => selectVariant(variant, questionNumber)}
				onKeyDown={() => selectVariant(variant, questionNumber)}
				tabIndex={0}
				type="button"
			>
				<div className={styles.Variant__VoteSlot}>
					{(isSelectedVariant || theme === 'corrected') && (
						<Icon
							icon={iconToShow}
							className={cx(
								'pvi-no-styles',
								cx(
									styles.Variant__VoteIcon,
									styles[`Variant__VoteIcon_${theme}`]
								)
							)}
						/>
					)}
				</div>
				{!_.isEmpty(jsonText) && (
					<span className={styles.Variant__Text}>
						{jsonText.map((block) => (
							<StyledContentBlock
								key={block?.text?.slice(0, 15) || block?.type}
								{...block}
							/>
						))}
					</span>
				)}
			</button>

			{!!imageSrc && (
				<div className={styles.Variant__WrapMedia}>
					<div
						className={styles.Variant__WrapImage}
						style={{ paddingBottom: `${getRatioImage(image)}%` }}
					>
						<StyledContentImage
							src={imageSrc}
							className={styles.Variant__Image}
						/>
					</div>
				</div>
			)}

			{!!video && video?._id && (
				<div className={styles.Variant__WrapMedia}>
					<div className={styles.Variant__WrapVideo}>
						<StyledContentVideo material={video} responsive autoPlay={false} />
					</div>
				</div>
			)}

			{!!trackUrl && (
				<AudioPlayer
					trackSrc={trackUrl}
					className={styles.Variant__Audio}
					autoPlay={false}
				/>
			)}
		</li>
	);
};

Variant.propTypes = {
	variant: VariantPropType,
	selectedVariants: PropTypes.arrayOf(
		PropTypes.oneOfType([VariantPropType, PropTypes.object])
	),
	selectVariant: PropTypes.func,
	questionNumber: PropTypes.number,
	isTestCorrect: PropTypes.bool,
	isQuestionSelected: PropTypes.bool,
	interactiveMode: PropTypes.string
};

export default Variant;
